import { useContext, useEffect, useRef, useState } from 'react';
import LayersContext from '../../context/LayersContext';
import { formatValue } from '../../utils/helpers';
import { MoreVertical, SlidersHorizontal } from 'lucide-react';
import refreshSvg from '../../assets/general/refresh.svg';
import printWhiteSvg from '../../assets/general/print-white.svg';
import alertSvg from '../../assets/general/metric-metadata-icon-alert.svg';
import ItemsTable from './ItemsTable';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { UnfoldVertical, FoldVertical, ChevronDown } from 'lucide-react';
import { useClickOutside } from '../../hooks/useClickOutside';
import { clearProgrammaticLayers, toggleProgrammaticLayer } from '../../utils/messages';

interface LayerPanelProps {
    sharedView: boolean;
    dashboardView: string;
    onPrintClick: () => void;
    expandedSection?: "metrics" | "layerPanel" | null;
    toggleSize?: () => void;
}

const LayerPanel: React.FC<LayerPanelProps> = ({ sharedView, dashboardView, onPrintClick, expandedSection, toggleSize }) => {
    const { layers, loading, refreshLayers } = useContext(LayersContext);

    // Layer Panel State
    const initialLoadCompleteRef = useRef(false);
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [activeLayer, setActiveLayer] = useState<string>('');
    // const [currentPage, setCurrentPage] = useState<number>(1);
    // @ts-ignore
    const [searchQuery, setSearchQuery] = useState<string>('');

    // Selected Category State for ItemsTable prop
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
    const [showMobileMenu, setShowMobileMenu] = useState(false);
    const [layerDropdownOpen, setLayerDropdownOpen] = useState(false);
    
    // Refs for click handler events
    const dropdownRef = useRef<HTMLDivElement>(null);
    const mobileMenuRef = useRef<HTMLDivElement>(null);
    const layerDropdownRef= useRef<HTMLDivElement>(null);

    const origin = '*';
    // const itemsPerPage = 10;

    // Get the layer items from the active layer
    const activeLayerData = layers?.find(layer => layer.layer_name === activeLayer);
    const layerItems = activeLayerData?.items || [];

    // Get summary info for the current layer
    const summary = activeLayerData?.summary?.length ? activeLayerData.summary[0] : null;

    // Update activeLayer when layers become available
    useEffect(() => {
        if (!activeLayer && layers.length > 0) {
            setActiveLayer(layers[0].layer_name);
        }
    }, [layers]);

    useEffect(() => {
        if (!loading && layers.length > 0) {
            initialLoadCompleteRef.current = true;
        }
    }, [loading, layers]);

    // Reset selected category when layerId changes
    useEffect(() => {
        if (!activeLayerData?.layer_id || !activeLayerData?.layer_name) return;

        // Skip on initial load
        if (!initialLoadCompleteRef.current) return;

        // Reset selected category
        setSelectedCategory(null);

        // Clear current programmatic layers
        clearProgrammaticLayers();

        // Toggle new programmatic layer
        toggleProgrammaticLayer(activeLayerData.layer_id, activeLayerData.layer_name);
    }, [activeLayerData?.layer_id]);

    useClickOutside(dropdownRef, () => setShowCategoryDropdown(false), showCategoryDropdown);
    useClickOutside(mobileMenuRef, () => setShowMobileMenu(false), showMobileMenu);
    useClickOutside(layerDropdownRef, () => setLayerDropdownOpen(false), layerDropdownOpen);

    // Handle active layer change from dropdown
    const handleLayerChange = (layerName: string) => {
        // Update activeLayer
        setActiveLayer(layerName);

        // Refresh layers
        refreshLayers();
    };

    // Filter layer items based on selected categories
    const filteredLayerItems = selectedCategories.length > 0
    ? layerItems?.filter(item => selectedCategories.includes(item.category))
    : layerItems;

    const searchedLayerItems = filteredLayerItems?.filter(item =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Sort the filtered items by activity_date (descending)
    const sortedLayerItems = searchedLayerItems?.sort((a, b) => {
        const dateA = new Date(a.activity_date);
        const dateB = new Date(b.activity_date);
        return dateB.getTime() - dateA.getTime();
    });

    // Calculate the total value of the filtered items
    const totalValue = filteredLayerItems?.reduce((sum, item) => sum + (item.value || 0), 0) || 0;

    // Paginate the filtered items
    const totalItems = filteredLayerItems?.length || 0;

    // Get unique categories from layerItems for the dropdown
    const categories = Array.from(
        new Set(
            (layerItems || [])
            .map(item => item.category)
            .filter(Boolean)
        )
    );

    const useDropdown = categories.length > 5;

    // Toggle category selection
    const handleCategoryToggle = (category: string) => {
        setSelectedCategories(prev =>
            prev.includes(category)
                ? prev.filter(c => c !== category) // Remove if already selected
                : [...prev, category] // Add if not selected
        );
    };

    const handleRefreshClick = () => {
        // Refresh layers using context function
        refreshLayers();

        // Send message to refresh programmatic layer
        window.parent.postMessage({ 
            message: "refreshProgrammaticLayer",
            data: {
                layerId: activeLayerData?.layer_id,
            }
        }, origin);
        console.log(`Refreshing programmatic layer for ${activeLayer}`);
    }

    const isReady = activeLayer && activeLayerData && !loading;

    const noItems =
    isReady &&
    layerItems.length === 0 &&
    layers.length > 0 &&
    !!activeLayerData;

    // Logic for if there are no items present in the selected layer
    if (!loading && layerItems.length === 0 && layers.length === 0) {
        return (
        <div className="flex flex-col items-start w-full p-6 pt-0">
            <div className="w-full border border-gray-200 bg-gray-50 rounded-xl p-4 shadow-sm flex items-center space-x-3">
                <img src={alertSvg} alt='Alert icon' className='w-3 h-3'/>
                <p className="text-sm text-port-text">
                    There are no active layers or items for the selected organization.
                </p>
            </div>
        </div>
        );
    }

    return (
        <>
            {/* Header */}
            <div className="sticky top-0 z-10 py-2 bg-white">
            {layers[0]?.layer_name ? (
                <header className="bg-dark-gg px-2 py-1 flex flex-wrap items-center justify-between gap-2 sm:flex-nowrap sm:justify-center relative">
                    {/* Title */}
                    {layers.length > 1 ? (
                    <div className="relative inline-block w-full tiny:w-auto" ref={layerDropdownRef}>
                        <button
                            onClick={() => setLayerDropdownOpen((prev) => !prev)}
                            className="text-sm font-bold bg-dark-gg text-white px-3 py-1 w-full tiny:w-auto flex items-center justify-between rounded shadow"
                            aria-haspopup="listbox"
                            aria-expanded={layerDropdownOpen}
                        >
                            {activeLayer}
                            <ChevronDown className="ml-2 w-4 h-4" />
                        </button>
                        {layerDropdownOpen && (
                        <ul
                            className="absolute z-10 mt-1 bg-white border border-gray-200 rounded shadow-md w-full tiny:min-w-[16rem] max-h-60 overflow-y-auto text-xs"
                            role="listbox"
                        >
                            {layers.map((layer) => (
                                <li
                                    key={layer.layer_name}
                                    role="option"
                                    aria-selected={activeLayer === layer.layer_name}
                                    onClick={() => {
                                    handleLayerChange(layer.layer_name);
                                    setLayerDropdownOpen(false);
                                    }}
                                    className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${
                                    activeLayer === layer.layer_name ? "bg-gray-200 font-semibold" : ""
                                    }`}
                                >
                                    {layer.layer_name}
                                </li>
                            ))}
                        </ul>
                        )}
                    </div>
                    ) : (
                        <h2 className="text-sm font-semibold text-white text-start w-full tiny:w-auto">
                            {layers[0]?.layer_name ?? 'Loading'}
                        </h2>
                    )}

                    {/* Buttons for tiny and up */}
                    <div className="hidden tiny:flex items-center space-x-2 absolute right-2 top-1/2 -translate-y-1/2">
                        <button
                            onClick={() => setShowCategoryDropdown((prev) => !prev)}
                            className="text-white hover:bg-white/20 transition-colors"
                            title="Filter by category"
                        >
                            <SlidersHorizontal className="w-5 h-5" />
                        </button>
                        <button
                            onClick={toggleSize}
                            className="text-white hover:bg-white/20 rounded-full transition-colors"
                            title={expandedSection === 'layerPanel' ? 'Collapse' : 'Expand'}
                        >
                            {expandedSection === 'layerPanel' ? (
                                <FoldVertical className="w-5 h-5" />
                            ) : (
                                <UnfoldVertical className="w-5 h-5" />
                            )}
                        </button>
                    </div>

                    {/* Mobile "More" menu trigger */}
                    <div ref={mobileMenuRef} className="tiny:hidden absolute right-2 mt-1">
                        <button
                            onClick={() => setShowMobileMenu((prev) => !prev)}
                            className="text-white hover:bg-white/20 rounded-full transition-colors"
                            title="More options"
                        >
                            <MoreVertical className="w-5 h-5" />
                        </button>

                        {/* Mobile dropdown menu */}
                        {showMobileMenu && (
                        <div className="absolute right-0 bg-white border rounded shadow-lg text-sm z-20 w-40">
                            <button
                                onClick={() => {
                                    setShowCategoryDropdown(true);
                                    setShowMobileMenu(false);
                                }}
                                className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                            >
                                Filter by Category
                            </button>
                            <button
                                onClick={() => {
                                    toggleSize?.();
                                    setShowMobileMenu(false);
                                }}
                                className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                            >
                                {expandedSection === 'layerPanel' ? 'Collapse' : 'Expand'}
                            </button>
                        </div>
                        )}
                    </div>

                    {/* Filter dropdown */}
                    {showCategoryDropdown && (
                        <div ref={dropdownRef} className="absolute right-10 top-full mt-1 bg-white border rounded shadow-lg text-xs z-20">
                            <div
                                className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                                selectedCategory === null ? 'font-bold' : ''
                                }`}
                                onClick={() => {
                                    setSelectedCategory(null);
                                    setShowCategoryDropdown(false);
                                }}
                            >
                                All Categories
                            </div>
                            {categories.map((cat) => (
                                <div
                                    key={cat}
                                    className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                                    selectedCategory === cat ? 'font-bold' : ''
                                    }`}
                                    onClick={() => {
                                        setSelectedCategory(cat);
                                        setShowCategoryDropdown(false);
                                    }}
                                >
                                    {cat}
                                </div>
                            ))}
                        </div>
                    )}
                </header>
            ) : (
                <Skeleton width="100%" height={28} />
            )}
            </div>
            
            <div className="grid grid-cols-3 px-6 py-0 w-full">
                {/* Text for no categories or items present */}
                {noItems &&
                <>
                    <p className='col-span-3 mt-4 m-auto text-xs text-port-text'>There are no items from <strong>{activeLayer}</strong> within your current selection.</p>
                    <p className='col-span-3 mt-2 m-auto text-xs text-port-text'>The map is still fully interactive—try widening, moving, or clearing your selection to uncover nearby items.</p>
                </>
                }
                {/* Hide Category and Summary in sharedView */}
                {!sharedView && (
                <>
                {/* Category Buttons */}
                {categories.length > 0 ?
                <div className="flex flex-col justify-between col-span-2">
                    <div className="flex flex-col">
                        {useDropdown ? (
                            // Render dropdown if more than 5 categories
                            <div className='flex mr-4 my-2'>
                            <select
                                onChange={(e) => handleCategoryToggle(e.target.value)}
                                className="border text-xs px-2 py-1 rounded-md shadow-md w-full outline-none text-port-text placeholder:text-port-green font-bold cursor-pointer"
                                value=""
                            >
                                <option value="" disabled>Select a category</option>
                                {categories.map((category, index) => (
                                    <option key={index} value={category}>
                                        {category}
                                    </option>
                                ))}
                            </select>
                            </div>
                        ) : (
                            // Render buttons for 5 or fewer categories
                            categories.map((category, index) => (
                                <button
                                    key={index}
                                    onClick={() => handleCategoryToggle(category)}
                                    className={`flex items-center gap-2 pr-3 mr-8 py-2 border-b text-left ${index === categories.length - 1 ? 'border-b-0' : ''}`}
                                >
                                    <span
                                        className={`w-4 h-4 shrink-0 flex items-center justify-center border border-port-green rounded ${selectedCategories.includes(category) ? "bg-port-green text-white" : "bg-white"}`}
                                    >
                                        {selectedCategories.includes(category) && <span>✔</span>}
                                    </span>
                                    <p className="text-xs font-bold text-port-text">{category}</p>
                                </button>
                            ))
                        )}
                    </div>
                    {layerItems.length > 0 &&
                        <div className='flex justify-start space-x-3'>
                            <button
                            onClick={onPrintClick}
                            disabled={loading}
                            className="col-span-1 col-start-3 mt-2 w-16 text-[10px] font-bold border border-port-green px-2 py-1 rounded-md shadow-md bg-port-green text-white transition"
                            >
                                <div className='flex items-center'>
                                    <img src={printWhiteSvg} alt='Print' className='w-4 mr-1'/>
                                    {'Print'}
                                </div>
                            </button>
                            <button
                                onClick={handleRefreshClick}
                                disabled={loading}
                                className="col-span-1 col-start-3 mt-2 w-28 text-[10px] font-bold border border-port-green px-2 py-1 rounded-md shadow-md bg-port-green text-white transition"
                            >
                                <div className='flex items-center'>
                                    <img src={refreshSvg} alt='Refresh' className='w-4 mr-1'/>
                                    {loading ? 'Refreshing...' : 'Refresh Items'}
                                </div>
                            </button>
                        </div>
                    } 
                </div> : 
                <>
                    {layerItems.length > 0 && 
                        <div className="mt-4 flex flex-col justify-center col-span-2">
                            <p className="text-xs text-port-text">
                                No categories available for <strong>{activeLayer}</strong>.
                            </p>
                        </div>
                    }
                </>
                }

                {/* Totals */}
                <div className="flex flex-col items-start justify-start col-span-1">
                    <div className={`flex flex-col items-start justify-center ${totalItems ? 'visible' : 'hidden'}`}>
                        <p className="text-[44px] font-bold text-port-blue font-barlow-condensed">
                            {formatValue(totalItems, 'integer')}
                        </p>
                        <p className='text-xs font-bold text-port-text -mt-2'>Total Items</p>
                    </div>

                    <div className={`flex flex-col items-start justify-center ${totalValue ? 'visible' : 'hidden'}`}>
                        <p className="text-[44px] font-bold text-port-blue font-barlow-condensed">
                            {formatValue(totalValue, summary?.unit ?? 
                            "integer")}
                        </p>
                        <p className='text-xs font-bold text-port-text -mt-2'>Total</p>
                        <p className='text-xs text-port-text'>{summary?.unit_descriptor}</p>
                    </div>
                </div>
                </>
                )}

                {/* Search Bar + Filter dropdown - Save for further reference */}
                {/* {layerItems.length > 0 ?
                    <div className="col-span-3 flex items-center mt-4 border rounded-lg p-2 bg-white shadow-lg">
                        <input
                            type="text"
                            placeholder="Search for sites…"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full outline-none px-2 text-xs text-port-text placeholder:text-port-green font-bold"
                        />
                        {searchQuery ? (
                            <button
                                onClick={() => setSearchQuery('')}
                                className="mx-1 text-gray-500 hover:text-port-green text-xs font-bold"
                            >
                                ✕
                            </button>
                        ) : (
                            <img className="w-4 h-4" src={searchSvg} alt='Search icon'/>
                        )}

                        <div className="relative ml-3 flex items-center">
                            <button className="text-gray-600 hover:text-port-green" onClick={() => setShowCategoryDropdown(prev => !prev)}>
                            <SlidersHorizontal className="w-4 h-4" color="#88b637" />
                            </button>
                            {showCategoryDropdown && (
                            <div className="absolute right-0 top-5 mt-2 bg-white border rounded shadow-lg text-xs z-10">
                                <div
                                className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${selectedCategory === null ? "font-bold" : ""}`}
                                onClick={() => { setSelectedCategory(null); setShowCategoryDropdown(prev => !prev); }}
                                >
                                All Categories
                                </div>
                                {["Near Term", "Mid Term", "Near Term - Unlikely to Convert"].map((cat) => (
                                <div
                                    key={cat}
                                    className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${selectedCategory === cat ? "font-bold" : ""}`}
                                    onClick={() => { setSelectedCategory(cat); setShowCategoryDropdown(prev => !prev); }}
                                >
                                    {cat}
                                </div>
                                ))}
                            </div>
                            )}
                        </div>
                    </div>
                    : <SearchBarSkeleton/>
                }                 */}
            </div>

            {/* ITEMS DISPLAY */}
            <div className='w-full grid grid-cols-3 pb-6'>
                <div className='col-span-3'>
                    <ItemsTable
                        items={sortedLayerItems ?? []} 
                        summary={summary}
                        layerId={activeLayerData?.layer_id ?? null}
                        dashboardView={dashboardView}
                        loading={loading}
                        selectedCategory={selectedCategory}
                        expandedSection={expandedSection} 
                    />
                </div>
            </div>
        </>      
    );
};

export default LayerPanel;